import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { apiFetch, isDemoSession } from "./api";
import AiAssistanceNotice from "./Components/AiAssistanceNotice";
import InlineNotice from "./Components/InlineNotice";
import { userFacingError } from "./userFacingError";
import { useUiLanguage } from "./i18n/UiLanguageContext";
import AacPlannerPage from "./AacPlannerPage";
import ELogo2 from "./assets/ELogo2.png";
import { demoClassInsightsPath } from "./demoNavigation";

const API_BASE = "/api";

type Student = {
    id: number;
    class_id: number;
    first_name: string;
    notes?: string;
    active: boolean;
};

type Assessment = {
    id: number;
    class_id: number;
    title: string;
    assessment_date: string | null; // YYYY-MM-DD
    total_marks: number | null;
    legacy_percentage_only?: boolean;
    result_count?: number;
    raw_mark_result_count?: number;
};

type ResultRow = {
    student_id: number;
    first_name: string;
    score_percent: number | null;
    raw_mark?: number | null;
    absent: boolean;
};

type InsightStudentRow = {
    student_id: number;
    first_name: string;
    average: number | null;
    taken: number;
    missed: number;
    latest: number | null;
};

type AtRiskRow = {
    student_id: number;
    first_name: string;
    average: number | null;
    missed: number;
    reasons: string[];
};

type InsightsPayload = {
    class_id: number;
    class_average: number | null;
    assessment_count: number;
    active_student_count: number;
    student_rankings: InsightStudentRow[];
    at_risk: AtRiskRow[];
};

type StudentHistoryPoint = {
    assessment_id: number;
    title: string;
    date: string | null;
    student: number | null;
    absent: boolean;
    class_avg: number | null;
};

type StudentHistoryResp = {
    student: { id: number; first_name: string };
    points: StudentHistoryPoint[];
};

type ReportLength = "Short" | "Medium" | "Long";

type StudentReportDraft = {
    length: ReportLength;
    indicators: string[];
    signOff: string;
    comment: string;
    aiGenerated?: boolean;
};

const REPORT_INDICATORS = [
    "Has more ability",
    "Not working hard enough",
    "Lacks concentration",
    "Poor attendance",
    "Disruptive in class",
    "Excellent performer",
    "Always well behaved",
    "Participates well in class",
    "Willing to help others",
    "Improving steadily",
];

type DemoReportPlan = { strength: string; next: string; encouragement: string; indicators: string[] };

// These are fixed fictional teacher-selected indicators for the public demo.
// They are deliberately separate from the normal account draft state and are
// never written to browser storage or to the API.
const DEMO_REPORT_PLANS: Record<string, DemoReportPlan> = {
    "Aisling Byrne": { strength: "This latest result is particularly encouraging and shows that she can apply the key ideas successfully.", next: "Her next step is to explain scientific reasoning as clearly as she presents final answers, using precise terminology and linking conclusions to evidence.", encouragement: "Aisling should be pleased with these results and aim to maintain this standard in future assessments.", indicators: ["Excellent performer", "Participates well in class"] },
    "Ben Kavanagh": { strength: "He has demonstrated a strong grasp of the core ideas and can respond accurately when questions require more than one step.", next: "To extend further, he should practise unfamiliar application questions and make his reasoning explicit before choosing a final answer.", encouragement: "Ben has an excellent foundation for the next stage of his learning.", indicators: ["Excellent performer", "Participates well in class"] },
    "Ciara Doyle": { strength: "She selects appropriate methods confidently and shows a secure understanding of the topic, including in extended responses.", next: "Her next step is to compare possible explanations more deliberately and justify why the strongest conclusion is supported by the available evidence.", encouragement: "Ciara should continue to challenge herself with unfamiliar applications.", indicators: ["Excellent performer", "Willing to help others"] },
    "Darragh Flynn": { strength: "He has performed at a very high standard across the recorded tasks and applies the main ideas accurately.", next: "To deepen this work, he should develop fuller explanations that connect each answer to the relevant principle or piece of evidence.", encouragement: "Darragh has a strong platform from which to extend his learning.", indicators: ["Excellent performer", "Participates well in class"] },
    "Ella Moran": { strength: "She has achieved full marks across the recorded assessments, demonstrating a confident command of the central concepts.", next: "A useful next step is to seek out less familiar problems and explain not only what works, but why alternative approaches are less suitable.", encouragement: "Ella should be proud of this sustained high standard.", indicators: ["Excellent performer", "Willing to help others"] },
    "Fionn Walsh": { strength: "He has achieved full marks across the recorded assessments and shows a very secure understanding of the key material.", next: "To keep extending this strength, he should practise communicating a complete chain of reasoning in unfamiliar, multi-step questions.", encouragement: "Fionn is well placed to take on suitably challenging work.", indicators: ["Excellent performer", "Participates well in class"] },
    "Grace Nolan": { strength: "She has produced very strong work across the recorded assessments and demonstrates confident use of the main ideas.", next: "Her next step is to add more detail when evaluating evidence, making clear how each point supports the final conclusion.", encouragement: "Grace has an excellent basis for continued progress.", indicators: ["Excellent performer", "Willing to help others"] },
    "Hugo Keane": { strength: "He has performed at a high level throughout the recorded assessments and can apply the central concepts accurately.", next: "To develop further, he should practise unfamiliar applications that require a carefully justified conclusion rather than a concise final response.", encouragement: "Hugo should continue to build on this impressive attainment.", indicators: ["Excellent performer", "Participates well in class"] },
};

function preparedDemoComment(studentName: string, average: number | null | undefined, latest: number | null | undefined) {
    const firstName = studentName.split(/\s+/)[0] || studentName;
    const plan = DEMO_REPORT_PLANS[studentName];
    if (!plan) return `${firstName} has a prepared fictional report example available in this demo.`;
    const evidence = average == null || latest == null
        ? `${firstName} has limited recorded assessment evidence at present.`
        : `Across the three recorded assessments, ${firstName} has an average of ${Math.round(average)}% and achieved ${Math.round(latest)}% in the most recent end-of-topic assessment.`;
    return `${evidence} ${plan.strength} ${plan.next} ${plan.encouragement}`;
}

export default function ClassAdminPage() {
    const { t, language } = useUiLanguage();
    const translate = t;
    const { id } = useParams<{ id: string }>();
    const location = useLocation();
    const classId = useMemo(() => Number(id), [id]);
    const validClassId = Number.isFinite(classId) && classId > 0;
    const [studentToken, setStudentToken] = useState<string | null>(null);

    const navigate = useNavigate();

    const [tab, setTab] = useState<"students" | "tests" | "insights" | "aac" | "reports">(() => {
        if (location.pathname.endsWith("/admin/aac")) return "aac";
        return new URLSearchParams(location.search).get("tab") === "insights" ? "insights" : "students";
    });
    const [aacEnabled, setAacEnabled] = useState(false);

    const [students, setStudents] = useState<Student[]>([]);
    const [tests, setTests] = useState<Assessment[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [insights, setInsights] = useState<InsightsPayload | null>(null);
    const [insightsLoading, setInsightsLoading] = useState(false);
    const [insightsError, setInsightsError] = useState<string | null>(null);
    const [cat4Enabled, setCat4Enabled] = useState(false);
    const [isDemoAccount, setIsDemoAccount] = useState(false);
    const [demoIdentityResolved, setDemoIdentityResolved] = useState(() => isDemoSession());
    // Read the tab-scoped demo token as an immediate capability signal too.
    // /auth/me remains the authority, but this prevents a brief first-render
    // window from exposing editable-looking controls while it is loading.
    const demoReadOnly = !demoIdentityResolved || isDemoAccount || isDemoSession();
    const demoWorkspace = isDemoAccount || isDemoSession();

    const [historyCache, setHistoryCache] = useState<Record<number, StudentHistoryResp | null>>({});
    const [historyLoading, setHistoryLoading] = useState<Record<number, boolean>>({});
    const [selectedHistoryStudent, setSelectedHistoryStudent] = useState<InsightStudentRow | null>(null);
    const [editingStudentName, setEditingStudentName] = useState<Student | null>(null);
    const [editedStudentName, setEditedStudentName] = useState("");
    const [savingStudentName, setSavingStudentName] = useState(false);
    const [studentNameError, setStudentNameError] = useState<string | null>(null);

    // quick add student
    const [firstName, setFirstName] = useState("");
    const [notes, setNotes] = useState("");
    const [bulkNames, setBulkNames] = useState("");
    const [deletingStudent, setDeletingStudent] = useState<Student | null>(null);
    const [deleteBusy, setDeleteBusy] = useState(false);

    // ---- Assessments / Results UI ----
    const [showCreateTest, setShowCreateTest] = useState(false);
    const [newTestTitle, setNewTestTitle] = useState("");
    const [newTestDate, setNewTestDate] = useState("");
    const [newTestTotalMarks, setNewTestTotalMarks] = useState("100");

    const [showEditTest, setShowEditTest] = useState(false);
    const [editingTest, setEditingTest] = useState<Assessment | null>(null);
    const [editTestTitle, setEditTestTitle] = useState("");
    const [editTestDate, setEditTestDate] = useState("");
    const [editTestTotalMarks, setEditTestTotalMarks] = useState("");
    const [savingEditTest, setSavingEditTest] = useState(false);
    const [confirmingTotalMarksChange, setConfirmingTotalMarksChange] = useState(false);
    const [editTestError, setEditTestError] = useState<string | null>(null);
    const [editTestReturnsToResults, setEditTestReturnsToResults] = useState(false);
    const [resultDraftNotice, setResultDraftNotice] = useState<string | null>(null);

    const [showResults, setShowResults] = useState(false);
    const [selectedAssessment, setSelectedAssessment] = useState<Assessment | null>(null);
    const [resultRows, setResultRows] = useState<ResultRow[]>([]);
    const [markDrafts, setMarkDrafts] = useState<Record<number, string>>({});
    const [savingResults, setSavingResults] = useState(false);
    const reportDraftsKey = `elume_report_drafts_class_${classId}`;

    // Do not read a normal teacher's browser drafts before the authenticated
    // identity is known. Demo comments are prepared in memory below.
    const [reportDrafts, setReportDrafts] = useState<Record<number, StudentReportDraft>>({});

    const [generatingReportFor, setGeneratingReportFor] = useState<number | null>(null);
    const [reportError, setReportError] = useState<string | null>(null);
    const [reportQuotaNotice, setReportQuotaNotice] = useState<string | null>(null);

    const activeCount = students.filter((s) => s.active).length;
    const inactiveCount = students.length - activeCount;
    const sortedAtRiskStudents = useMemo(() => {
        return [...(insights?.at_risk || [])].sort((a, b) => {
            const aAvg = a.average ?? Number.POSITIVE_INFINITY;
            const bAvg = b.average ?? Number.POSITIVE_INFINITY;
            if (aAvg !== bAvg) return aAvg - bAvg;
            if (a.missed !== b.missed) return b.missed - a.missed;
            return a.first_name.localeCompare(b.first_name, undefined, { sensitivity: "base" });
        });
    }, [insights?.at_risk]);

    const card =
        "rounded-3xl border-2 border-slate-200 bg-white shadow-[0_2px_0_rgba(15,23,42,0.06)]";
    const cardPad = "p-4 md:p-5";
    const pill =
        "rounded-full border-2 border-slate-200 bg-white px-5 py-2 text-sm hover:bg-slate-50";

    useEffect(() => {
        if (tab !== "insights" && tab !== "reports") return;
        loadInsights();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab, classId]);

    useEffect(() => {
        let cancelled = false;
        void apiFetch("/auth/me")
            .then((account) => {
                if (cancelled) return;
                const demo = Boolean(account?.is_demo_account) || isDemoSession();
                setIsDemoAccount(demo);
                setDemoIdentityResolved(true);
                if (!demo) {
                    try {
                        const raw = localStorage.getItem(`elume_report_drafts_class_${classId}`);
                        setReportDrafts(raw ? JSON.parse(raw) : {});
                    } catch {
                        setReportDrafts({});
                    }
                }
            })
            .catch(() => { if (!cancelled) setDemoIdentityResolved(false); });
        return () => { cancelled = true; };
    }, [classId]);

    useEffect(() => {
        if (tab !== "insights" || !validClassId) return;

        let cancelled = false;
        apiFetch(`${API_BASE}/classes/${classId}/cat4/meta`)
            .then((data) => {
                if (cancelled) return;
                setCat4Enabled(Boolean(data?.feature_enabled));
            })
            .catch(() => {
                if (cancelled) return;
                setCat4Enabled(false);
            });

        return () => {
            cancelled = true;
        };
    }, [tab, classId, validClassId]);

    useEffect(() => {
        loadAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [classId, validClassId]);

    useEffect(() => {
        if (!validClassId) return;

        apiFetch(`${API_BASE}/student-access/${classId}`)
            .then((data) => setStudentToken(data.token))
            .catch(() => { });
    }, [classId, validClassId]);

    // AAC access and enablement must be discovered before the planner mounts:
    // the dedicated tab itself is what makes the planner mount. The server
    // remains authoritative here; a denied or failed request never reveals it.
    useEffect(() => {
        if (!validClassId) {
            setAacEnabled(false);
            return;
        }
        let cancelled = false;
        setAacEnabled(false);
        apiFetch(`${API_BASE}/classes/${classId}/aac`)
            .then((data) => {
                if (cancelled) return;
                const enabled = data?.enabled === true;
                setAacEnabled(enabled);
                if (!enabled && location.pathname.endsWith("/admin/aac")) setTab("insights");
            })
            .catch(() => {
                if (cancelled) return;
                setAacEnabled(false);
                if (location.pathname.endsWith("/admin/aac")) setTab("insights");
            });
        return () => {
            cancelled = true;
        };
    }, [classId, validClassId, location.pathname]);

    useEffect(() => {
        setReportDrafts((prev) => {
            const next = { ...prev };

            for (const s of students) {
                if (!next[s.id]) {
                    next[s.id] = {
                        length: "Medium",
                        indicators: [],
                        signOff: "",
                        comment: "",
                    };
                }
            }

            return next;
        });
    }, [students]);

    useEffect(() => {
        if (demoReadOnly) return;
        try {
            localStorage.setItem(reportDraftsKey, JSON.stringify(reportDrafts));
        } catch {
            // ignore storage errors
        }
    }, [demoReadOnly, reportDrafts, reportDraftsKey]);

    useEffect(() => {
        if (tab !== "insights" && tab !== "reports") return;
        loadInsights();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab, classId, validClassId]);

    async function fetchStudentHistory(studentId: number) {
        if (!validClassId) return;
        if (historyCache[studentId] || historyLoading[studentId]) return;

        setHistoryLoading((p) => ({ ...p, [studentId]: true }));

        try {
            const data = (await apiFetch(
                `${API_BASE}/classes/${classId}/students/${studentId}/history`
            )) as StudentHistoryResp;

            setHistoryCache((p) => ({ ...p, [studentId]: data }));
        } catch {
            setHistoryCache((p) => ({ ...p, [studentId]: null }));
        } finally {
            setHistoryLoading((p) => ({ ...p, [studentId]: false }));
        }
    }

    async function openStudentHistoryModal(student: InsightStudentRow) {
        setSelectedHistoryStudent(student);
        await fetchStudentHistory(student.student_id);
    }

    async function loadAll() {
        if (!validClassId) return;
        setLoading(true);
        setError(null);

        try {
            const [sdata, tdata] = await Promise.all([
                apiFetch(`${API_BASE}/classes/${classId}/students`),
                apiFetch(`${API_BASE}/classes/${classId}/assessments`),
            ]);

            setStudents(Array.isArray(sdata) ? sdata : []);
            setTests(Array.isArray(tdata) ? tdata : []);
        } catch (error: unknown) {
            setError(userFacingError(error, "We couldn’t load this class just yet. Give it another try."));
            setStudents([]);
            setTests([]);
        } finally {
            setLoading(false);
        }
    }

    async function generateStudentLink() {
        if (demoReadOnly) return;
        const data = await apiFetch(`${API_BASE}/student-access/${classId}`, {
            method: "POST",
        });
        setStudentToken(data.token);
    }

    async function addStudent() {
        if (demoReadOnly) {
            setError(t("classAdmin.demoRosterLocked"));
            return;
        }
        const fn = firstName.trim();
        if (!fn || !validClassId) return;

        try {
            setError(null);
            const created = await apiFetch(`${API_BASE}/classes/${classId}/students`, {
                method: "POST",
                body: JSON.stringify({ first_name: fn, notes: notes.trim() }),
            });
            setStudents((prev) => [...prev, created]);
            setFirstName("");
            setNotes("");
        } catch (error: unknown) {
            setError(userFacingError(error, "We couldn’t add that student just now. Please try again."));
        }
    }

    async function addStudentsBulk() {
        if (demoReadOnly) {
            setError(t("classAdmin.demoRosterLocked"));
            return;
        }
        if (!validClassId) return;

        const names = bulkNames
            .split(/\r?\n|,/)
            .map((s) => s.trim())
            .filter(Boolean);

        if (names.length === 0) return;

        try {
            setError(null);
            const created = await apiFetch(`${API_BASE}/classes/${classId}/students/bulk`, {
                method: "POST",
                body: JSON.stringify({ names }),
            });
            if (Array.isArray(created) && created.length) {
                setStudents((prev) => [...prev, ...created]);
            }
            setBulkNames("");
        } catch (error: unknown) {
            setError(userFacingError(error, "We couldn’t add those students just now. Please try again."));
        }
    }

    async function toggleActive(studentId: number, active: boolean) {
        if (demoReadOnly) {
            setError(t("classAdmin.demoRosterLocked"));
            return;
        }
        try {
            setError(null);
            const updated = await apiFetch(`${API_BASE}/students/${studentId}`, {
                method: "PUT",
                body: JSON.stringify({ active }),
            });
            setStudents((prev) => prev.map((s) => (s.id === studentId ? updated : s)));
        } catch (error: unknown) {
            setError(userFacingError(error, "We couldn’t update that student just now. Please try again."));
        }
    }

    function openStudentNameEditor(insightStudent: InsightStudentRow) {
        if (demoReadOnly) {
            setError(t("classAdmin.demoRosterLocked"));
            return;
        }
        const rosterStudent = students.find((student) => student.id === insightStudent.student_id);
        if (!rosterStudent) {
            setError(t("classAdmin.studentNameUnavailable"));
            return;
        }

        setEditedStudentName(rosterStudent.first_name);
        setStudentNameError(null);
        setEditingStudentName(rosterStudent);
    }

    function closeStudentNameEditor() {
        if (savingStudentName) return;
        setEditingStudentName(null);
        setStudentNameError(null);
    }

    async function saveStudentName() {
        if (demoReadOnly) {
            setStudentNameError(t("classAdmin.demoRosterLocked"));
            return;
        }
        if (!editingStudentName || savingStudentName) return;

        const firstName = editedStudentName.trim();
        if (!firstName) {
            setStudentNameError(t("classAdmin.studentNameRequired"));
            return;
        }

        try {
            setSavingStudentName(true);
            setStudentNameError(null);
            const updated = (await apiFetch(`${API_BASE}/students/${editingStudentName.id}`, {
                method: "PUT",
                body: JSON.stringify({ first_name: firstName }),
            })) as Student;

            setStudents((prev) => prev.map((student) => (student.id === updated.id ? updated : student)));
            setSelectedHistoryStudent((prev) =>
                prev?.student_id === updated.id ? { ...prev, first_name: updated.first_name } : prev
            );
            setHistoryCache((prev) => {
                const current = prev[updated.id];
                return current
                    ? { ...prev, [updated.id]: { ...current, student: { ...current.student, first_name: updated.first_name } } }
                    : prev;
            });
            setInsights((prev) =>
                prev
                    ? {
                        ...prev,
                        student_rankings: prev.student_rankings.map((student) =>
                            student.student_id === updated.id ? { ...student, first_name: updated.first_name } : student
                        ),
                        at_risk: prev.at_risk.map((student) =>
                            student.student_id === updated.id ? { ...student, first_name: updated.first_name } : student
                        ),
                    }
                    : prev
            );
            setEditingStudentName(null);
            void loadInsights();
        } catch (error: unknown) {
            setStudentNameError(userFacingError(error, t("classAdmin.studentNameSaveFailed")));
        } finally {
            setSavingStudentName(false);
        }
    }

    async function confirmDeleteStudent() {
        if (demoReadOnly) {
            setError(t("classAdmin.demoRosterLocked"));
            setDeletingStudent(null);
            return;
        }
        if (!deletingStudent) return;

        try {
            setDeleteBusy(true);
            setError(null);
            await apiFetch(`${API_BASE}/students/${deletingStudent.id}`, {
                method: "DELETE",
            });
            setStudents((prev) => prev.filter((s) => s.id !== deletingStudent.id));
            setHistoryCache((prev) => {
                const next = { ...prev };
                delete next[deletingStudent.id];
                return next;
            });
            setHistoryLoading((prev) => {
                const next = { ...prev };
                delete next[deletingStudent.id];
                return next;
            });
            setReportDrafts((prev) => {
                const next = { ...prev };
                delete next[deletingStudent.id];
                return next;
            });
            if (selectedHistoryStudent?.student_id === deletingStudent.id) {
                setSelectedHistoryStudent(null);
            }
            setDeletingStudent(null);
            if (tab === "insights" || tab === "reports") {
                void loadInsights();
            }
        } catch (error: unknown) {
            setError(userFacingError(error, "We couldn’t remove that student just now. Please try again."));
        } finally {
            setDeleteBusy(false);
        }
    }

    async function createAssessment() {
        if (demoReadOnly) {
            setError("This demo uses prepared assessments so you can explore results and progress. In your own Elume account, you can create and edit assessments.");
            return;
        }
        if (!validClassId) return;
        const title = newTestTitle.trim();
        if (!title) return;
        const totalMarks = Number(newTestTotalMarks);
        if (!Number.isFinite(totalMarks) || totalMarks <= 0) {
            setError(t("classAdmin.totalMarksRequired"));
            return;
        }

        try {
            setError(null);
            const created = await apiFetch(`${API_BASE}/classes/${classId}/assessments`, {
                method: "POST",
                body: JSON.stringify({
                    title,
                    assessment_date: newTestDate.trim() || null,
                    total_marks: totalMarks,
                }),
            });

            setTests((prev) => [created, ...prev]);

            setNewTestTitle("");
            setNewTestDate("");
            setNewTestTotalMarks("100");
            setShowCreateTest(false);
        } catch (error: unknown) {
            setError(userFacingError(error, "We couldn’t create that assessment just now. Please try again."));
        }
    }

    function openEditTestModal(test: Assessment, returnToResults = false) {
        if (demoReadOnly) {
            setError("This demo uses prepared assessments so you can explore results and progress. In your own Elume account, you can edit assessments.");
            return;
        }
        if (returnToResults) {
            // Results rows and drafts remain in state while the one visible dialog
            // switches to the editor.
            setShowResults(false);
            setResultDraftNotice(null);
        }
        setEditingTest(test);
        setEditTestTitle(test.title || "");
        setEditTestDate(test.assessment_date || "");
        setEditTestTotalMarks(test.total_marks == null ? "" : String(test.total_marks));
        setConfirmingTotalMarksChange(false);
        setEditTestError(null);
        setEditTestReturnsToResults(returnToResults);
        setShowEditTest(true);
    }

    function closeEditTestModal() {
        const returnToResults = editTestReturnsToResults && selectedAssessment?.id === editingTest?.id;
        setShowEditTest(false);
        setEditingTest(null);
        setConfirmingTotalMarksChange(false);
        setEditTestError(null);
        setEditTestReturnsToResults(false);
        if (returnToResults) {
            setResultDraftNotice(t("classAdmin.unsavedResultsKept"));
            setShowResults(true);
        }
    }

    function recalculateResultPreviews(totalMarks: number) {
        setResultRows((previousRows) => previousRows.map((row) => {
            if (row.absent) return row;
            const draft = markDrafts[row.student_id];
            if (draft !== undefined) {
                const trimmed = draft.trim();
                if (!trimmed) return { ...row, raw_mark: null, score_percent: null };
                const rawMark = Number(trimmed);
                if (Number.isFinite(rawMark)) {
                    return { ...row, raw_mark: rawMark, score_percent: (rawMark / totalMarks) * 100 };
                }
                return row;
            }
            if (row.raw_mark != null) {
                return { ...row, score_percent: (row.raw_mark / totalMarks) * 100 };
            }
            return row;
        }));
    }

    async function saveEditedTest(confirmedTotalChange = false) {
        if (demoReadOnly) {
            setEditTestError("Prepared demo assessments cannot be changed.");
            return;
        }
        if (!editingTest) return;

        const title = editTestTitle.trim();
        if (!title) return;
        const legacyLocked = editingTest.total_marks == null && (editingTest.result_count || 0) > 0;
        const totalMarks = Number(editTestTotalMarks);
        if (!legacyLocked && (!Number.isFinite(totalMarks) || totalMarks <= 0)) {
            setEditTestError(t("classAdmin.totalMarksRequired"));
            return;
        }
        const needsTotalChangeConfirmation = editingTest.total_marks != null
            && totalMarks !== editingTest.total_marks
            && (editingTest.raw_mark_result_count || 0) > 0;
        if (needsTotalChangeConfirmation && !confirmedTotalChange) {
            setEditTestError(null);
            setConfirmingTotalMarksChange(true);
            return;
        }

        try {
            setSavingEditTest(true);
            setEditTestError(null);

            const updated = (await apiFetch(`${API_BASE}/assessments/${editingTest.id}`, {
                method: "PUT",
                body: JSON.stringify({
                    title,
                    assessment_date: editTestDate.trim() || null,
                    ...(legacyLocked ? {} : { total_marks: totalMarks }),
                }),
            })) as Assessment;

            setTests((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));

            if (selectedAssessment?.id === updated.id) {
                setSelectedAssessment(updated);
                if (updated.total_marks != null) {
                    recalculateResultPreviews(updated.total_marks);
                }
            }

            setShowEditTest(false);
            setEditingTest(null);
            setEditTestTitle("");
            setEditTestDate("");
            setEditTestTotalMarks("");
            setConfirmingTotalMarksChange(false);
            const returnToResults = editTestReturnsToResults && selectedAssessment?.id === updated.id;
            setEditTestReturnsToResults(false);
            if (returnToResults) {
                setResultDraftNotice(t("classAdmin.unsavedResultsKept"));
                setShowResults(true);
            }
        } catch (error: unknown) {
            setEditTestError(userFacingError(error, "We couldn’t update that assessment just now. Please try again."));
        } finally {
            setSavingEditTest(false);
        }
    }

    async function deleteAssessment(assessment: Assessment) {
        if (demoReadOnly) {
            setError("Prepared demo assessments cannot be deleted.");
            return;
        }
        const ok = window.confirm(
            `Delete "${assessment.title}"?\n\nThis should also remove its saved results.`
        );
        if (!ok) return;

        try {
            setError(null);

            await apiFetch(`${API_BASE}/assessments/${assessment.id}`, {
                method: "DELETE",
            });

            setTests((prev) => prev.filter((t) => t.id !== assessment.id));

            if (selectedAssessment?.id === assessment.id) {
                setShowResults(false);
                setSelectedAssessment(null);
                setResultRows([]);
            }

            if (tab === "insights") {
                loadInsights();
            }
        } catch (error: unknown) {
            setError(userFacingError(error, "We couldn’t delete that assessment just now. Please try again."));
        }
    }

    async function openResults(assessmentId: number) {
        try {
            setError(null);
            const data = await apiFetch(`${API_BASE}/assessments/${assessmentId}/results`);

            setSelectedAssessment(data.assessment);
            const rows = Array.isArray(data.results) ? data.results : [];
            setResultRows(rows);
            setMarkDrafts(Object.fromEntries(rows.map((row: ResultRow) => [row.student_id, String(data.assessment.total_marks == null ? row.score_percent ?? "" : row.raw_mark ?? "")])));
            setResultDraftNotice(null);
            setShowResults(true);
        } catch (error: unknown) {
            setError(userFacingError(error, "We couldn’t load those results just yet. Give it another try."));
        }
    }

    async function loadInsights() {
        if (!validClassId) return;

        setInsightsLoading(true);
        setInsightsError(null);

        try {
            const data = (await apiFetch(
                `${API_BASE}/classes/${classId}/insights`
            )) as InsightsPayload;
            setInsights(data);
        } catch (error: unknown) {
            setInsightsError(userFacingError(error, "We couldn’t load the class insights just yet. Give it another try."));
            setInsights(null);
        } finally {
            setInsightsLoading(false);
        }
    }

    function avgClass(avg: number | null | undefined) {
        if (avg == null) return "text-slate-400";
        if (avg < 50) return "text-red-700";
        if (avg < 70) return "text-amber-700";
        return "text-emerald-700";
    }

    function setRowScore(studentId: number, raw: string) {
        if (demoReadOnly) return;
        setMarkDrafts((prev) => ({ ...prev, [studentId]: raw }));
        if (raw.trim() === "" || raw === "." || raw === "-") return;
        setResultRows((prev) =>
            prev.map((r) => {
                if (r.student_id !== studentId) return r;

                const num = Number(raw);
                if (!Number.isFinite(num)) return r;
                if (selectedAssessment?.total_marks != null) {
                    return { ...r, raw_mark: num, score_percent: (num / selectedAssessment.total_marks) * 100, absent: false };
                }
                return { ...r, score_percent: num, absent: false };
            })
        );
    }

    function toggleAbsent(studentId: number) {
        if (demoReadOnly) return;
        setMarkDrafts((prev) => ({ ...prev, [studentId]: "" }));
        setResultRows((prev) =>
            prev.map((r) =>
                r.student_id === studentId
                    ? {
                        ...r,
                        absent: !r.absent,
                        score_percent: !r.absent ? null : r.score_percent,
                        raw_mark: !r.absent ? null : r.raw_mark,
                    }
                    : r
            )
        );
    }

    function clearStudentResult(studentId: number) {
        if (demoReadOnly) return;
        setMarkDrafts((prev) => ({ ...prev, [studentId]: "" }));
        setResultRows((prev) =>
            prev.map((r) =>
                r.student_id === studentId
                    ? {
                        ...r,
                        score_percent: null,
                        raw_mark: null,
                        absent: false,
                    }
                    : r
            )
        );
    }

    async function saveResults() {
        if (demoReadOnly) {
            setResultDraftNotice("Prepared demo results are read-only. In your own Elume account, you can enter and save marks.");
            return;
        }
        if (!selectedAssessment) return;

        try {
            setSavingResults(true);
            setError(null);

            const rawAssessment = selectedAssessment.total_marks != null;
            const payload = {
                expected_total_marks: selectedAssessment.total_marks,
                results: resultRows.map((r) => ({
                    student_id: r.student_id,
                    score_percent: rawAssessment ? undefined : (r.absent ? null : (markDrafts[r.student_id]?.trim() === "" ? null : Number(markDrafts[r.student_id]))),
                    raw_mark: rawAssessment ? (r.absent || markDrafts[r.student_id]?.trim() === "" ? null : Number(markDrafts[r.student_id])) : undefined,
                    absent: !!r.absent,
                })),
            };

            await apiFetch(`${API_BASE}/assessments/${selectedAssessment.id}/results`, {
                method: "PUT",
                body: JSON.stringify(payload),
            });

            setShowResults(false);
            setSelectedAssessment(null);
            setResultRows([]);
            if (tab === "insights") {
                loadInsights();
            }
        } catch (error: unknown) {
            setError(userFacingError(error, "We couldn’t save those results just now. Please try again."));
        } finally {
            setSavingResults(false);
        }
    }

    function openClassReport() {
        if (demoWorkspace) {
            window.dispatchEvent(new CustomEvent("elume-demo-restricted", {
                detail: { message: translate("classAdmin.demoClassReportRestriction") },
            }));
            return;
        }
        window.open(`/#/class/${classId}/report`, "_blank", "noopener,noreferrer");
    }

    function openStudentReport(studentId: number) {
        if (demoWorkspace) {
            const returnTo = demoClassInsightsPath(classId);
            navigate(`/class/${classId}/student-report/${studentId}?returnTo=${encodeURIComponent(returnTo)}`);
            return;
        }
        window.open(`/#/class/${classId}/student-report/${studentId}`, "_blank", "noopener,noreferrer");
    }

    function openDemoCat4() {
        const returnTo = `/class/${classId}/admin`;
        navigate(`/demo/cat4-analysis?returnTo=${encodeURIComponent(returnTo)}`);
    }
    const insightByStudent = useMemo(() => {
        const m = new Map<number, InsightStudentRow>();
        for (const row of insights?.student_rankings || []) {
            m.set(row.student_id, row);
        }
        return m;
    }, [insights]);

    // Prepared comments make the real report screen useful without implying
    // that a demo visitor has generated or saved any pupil data.
    useEffect(() => {
        if (!demoReadOnly || students.length === 0) return;
        setReportDrafts((prev) => {
            const next = { ...prev };
            // Some older student payloads omit `active` for active records.
            // Treat only an explicit false as inactive so the fixed fixture is
            // populated consistently while preserving normal roster semantics.
            students.filter((student) => student.active !== false).forEach((student) => {
                const insight = insightByStudent.get(student.id);
                const plan = DEMO_REPORT_PLANS[student.first_name];
                next[student.id] = {
                    ...(next[student.id] || { length: "Medium", indicators: [], signOff: "", comment: "" }),
                    indicators: plan?.indicators || [],
                    comment: preparedDemoComment(student.first_name, insight?.average, insight?.latest),
                    aiGenerated: false,
                };
            });
            return next;
        });
    }, [demoReadOnly, students, insightByStudent]);

    function updateReportDraft(studentId: number, patch: Partial<StudentReportDraft>) {
        if (demoReadOnly) return;
        setReportDrafts((prev) => {
            const existing = prev[studentId];

            return {
                ...prev,
                [studentId]: existing
                    ? { ...existing, ...patch }
                    : {
                        length: "Medium",
                        indicators: [],
                        signOff: "",
                        comment: "",
                        ...patch,
                    },
            };
        });
    }
    function toggleReportIndicator(studentId: number, label: string) {
        if (demoReadOnly) return;
        const current = reportDrafts[studentId] || {
            length: "Medium" as ReportLength,
            indicators: [],
            signOff: "",
            comment: "",
        };

        const exists = current.indicators.includes(label);

        updateReportDraft(studentId, {
            indicators: exists
                ? current.indicators.filter((x) => x !== label)
                : [...current.indicators, label],
        });
    }

    async function generateReportComment(studentId: number) {
        const draft = reportDrafts[studentId];
        if (!draft) return;

        if (isDemoSession()) {
            window.dispatchEvent(new CustomEvent("elume-demo-restricted", { detail: { message: "This demo shows prepared report comments. In your own Elume account, you can generate and refine comments for your students." } }));
            return;
        }

        try {
            setGeneratingReportFor(studentId);
            setReportError(null);

            const data = await apiFetch(
                `${API_BASE}/classes/${classId}/students/${studentId}/generate-report-comment`,
                {
                    method: "POST",
                    body: JSON.stringify({
                        length: draft.length,
                        indicators: draft.indicators,
                        sign_off: draft.signOff.trim() || null,
                    }),
                }
            );

            updateReportDraft(studentId, {
                comment: data.comment || "",
                aiGenerated: Boolean(String(data.comment || "").trim()),
            });
            void apiFetch("/ai/usage/report_comment")
                .then((usage: { message?: string | null }) => setReportQuotaNotice(usage.message || null))
                .catch(() => undefined);
        } catch (error: unknown) {
            setReportError(userFacingError(error, "We couldn’t create that report comment just now. Please try again."));
        } finally {
            setGeneratingReportFor(null);
        }
    }

    type Trend = "up" | "down" | "flat" | "none";

    function pct(v: number | null | undefined) {
        if (v == null || !Number.isFinite(v)) return "—";
        return `${v.toFixed(1)}%`;
    }

    function avgPillClass(avg: number | null) {
        if (avg == null) return "bg-slate-50 border-slate-200 text-slate-700";
        if (avg >= 85) return "bg-emerald-50 border-emerald-200 text-emerald-800";
        if (avg >= 70) return "bg-sky-50 border-sky-200 text-sky-800";
        if (avg >= 50) return "bg-amber-50 border-amber-200 text-amber-900";
        return "bg-rose-50 border-rose-200 text-rose-800";
    }

    function trendFromLatest(avg: number | null, latest: number | null): Trend {
        if (avg == null || latest == null) return "none";
        const d = latest - avg;
        if (d >= 3) return "up";
        if (d <= -3) return "down";
        return "flat";
    }

    function TrendIcon({ t }: { t: Trend }) {
        if (t === "none") return null;

        const common = "h-4 w-4";
        if (t === "up")
            return (
                <svg className={common} viewBox="0 0 24 24" fill="none">
                    <path
                        d="M7 14l5-5 5 5"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            );

        if (t === "down")
            return (
                <svg className={common} viewBox="0 0 24 24" fill="none">
                    <path
                        d="M7 10l5 5 5-5"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            );

        return (
            <svg className={common} viewBox="0 0 24 24" fill="none">
                <path d="M6 12h12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
        );
    }

    function trendText(t: Trend) {
        if (t === "up") return "Improving";
        if (t === "down") return "Dropping";
        if (t === "flat") return "Stable";
        return "";
    }

    function trendClass(t: Trend) {
        if (t === "up") return "text-emerald-700";
        if (t === "down") return "text-rose-700";
        if (t === "flat") return "text-slate-600";
        return "text-slate-400";
    }

    function PerformanceChart({ points }: { points: StudentHistoryPoint[] }) {
        const width = 760;
        const height = 270;
        const margin = { top: 22, right: 24, bottom: 38, left: 48 };
        const plotWidth = width - margin.left - margin.right;
        const plotHeight = height - margin.top - margin.bottom;
        const gridValues = [0, 25, 50, 75, 100];
        const visibleLabelIndexes = points.length <= 5
            ? points.map((_, index) => index)
            : Array.from(new Set([0, Math.round((points.length - 1) / 2), points.length - 1]));

        const xTo = (index: number) => margin.left + (points.length <= 1 ? plotWidth / 2 : (index / (points.length - 1)) * plotWidth);
        const yTo = (value: number) => margin.top + (1 - Math.max(0, Math.min(100, value)) / 100) * plotHeight;
        const makePath = (getter: (point: StudentHistoryPoint) => number | null) => {
            let hasOpenSegment = false;
            return points.reduce((path, point, index) => {
                const value = getter(point);
                if (typeof value !== "number") {
                    hasOpenSegment = false;
                    return path;
                }
                const command = hasOpenSegment ? "L" : "M";
                hasOpenSegment = true;
                return `${path}${command}${xTo(index).toFixed(1)} ${yTo(value).toFixed(1)} `;
            }, "");
        };
        const shortLabel = (point: StudentHistoryPoint, index: number) => {
            if (point.date) {
                const date = new Date(`${point.date}T00:00:00`);
                if (!Number.isNaN(date.getTime())) {
                    return date.toLocaleDateString(language === "ga" ? "ga-IE" : "en-IE", { day: "numeric", month: "short" });
                }
            }
            return `Assessment ${index + 1}`;
        };

        return (
            <section className="rounded-2xl border border-emerald-100 bg-gradient-to-br from-emerald-50/80 via-white to-cyan-50/70 p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                        <h3 className="text-base font-extrabold text-slate-900">{t("classAdmin.performanceOverTime")}</h3>
                        <p className="mt-0.5 text-xs text-slate-600">{t("classAdmin.performanceHelp")}</p>
                    </div>
                    <div className="flex flex-wrap items-center gap-3 text-xs font-semibold text-slate-700" aria-label={t("classAdmin.chartLegend")}>
                        <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-emerald-600" />{t("classAdmin.student")}</span>
                        <span className="inline-flex items-center gap-1.5"><span className="w-5 border-t-2 border-dashed border-slate-500" />{t("classAdmin.classAverage")}</span>
                    </div>
                </div>
                <div className="mt-3 h-[250px] w-full">
                    <svg viewBox={`0 0 ${width} ${height}`} className="h-full w-full" role="img" aria-label={t("classAdmin.chartDescription")}>
                        {gridValues.map((value) => (
                            <g key={value}>
                                <line x1={margin.left} x2={width - margin.right} y1={yTo(value)} y2={yTo(value)} stroke="#cbd5e1" strokeWidth="1" strokeDasharray={value === 0 ? "0" : "3 5"} />
                                <text x={margin.left - 10} y={yTo(value) + 4} textAnchor="end" fill="#64748b" fontSize="12" fontWeight="600">{value}%</text>
                            </g>
                        ))}
                        <path d={makePath((point) => point.class_avg)} fill="none" stroke="#64748b" strokeWidth="2.5" strokeDasharray="8 7" strokeLinecap="round" strokeLinejoin="round" />
                        <path d={makePath((point) => point.student)} fill="none" stroke="#059669" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" />
                        {points.map((point, index) => typeof point.class_avg === "number" && (
                            <circle key={`class-${point.assessment_id}`} cx={xTo(index)} cy={yTo(point.class_avg)} r="3" fill="#ffffff" stroke="#64748b" strokeWidth="2" />
                        ))}
                        {points.map((point, index) => typeof point.student === "number" && (
                            <g key={point.assessment_id} tabIndex={0} aria-label={`${point.title}: student ${point.student} percent${typeof point.class_avg === "number" ? `, class average ${point.class_avg} percent` : ""}`}>
                                <title>{`${point.title}${point.date ? ` (${point.date})` : ""}: Student ${point.student}%${typeof point.class_avg === "number" ? `, class average ${point.class_avg}%` : ""}`}</title>
                                <circle cx={xTo(index)} cy={yTo(point.student)} r="5" fill="#ffffff" stroke="#059669" strokeWidth="3" />
                            </g>
                        ))}
                        {visibleLabelIndexes.map((index) => (
                            <text key={index} x={xTo(index)} y={height - 12} textAnchor="middle" fill="#64748b" fontSize="11" fontWeight="600">
                                {shortLabel(points[index], index)}
                            </text>
                        ))}
                    </svg>
                </div>
            </section>
        );
    }

    const resultsEnteredCount = resultRows.filter(
        (r) => r.absent || typeof r.score_percent === "number"
    ).length;

    const numericScores = resultRows
        .filter((r) => !r.absent && typeof r.score_percent === "number")
        .map((r) => r.score_percent as number);

    const modalAverage =
        numericScores.length > 0
            ? numericScores.reduce((sum, n) => sum + n, 0) / numericScores.length
            : null;

    const hasRawDraftAboveTotal = selectedAssessment?.total_marks != null && resultRows.some((row) => {
        if (row.absent) return false;
        const draft = markDrafts[row.student_id];
        if (draft === undefined || !draft.trim()) return false;
        const rawMark = Number(draft);
        return Number.isFinite(rawMark) && rawMark > selectedAssessment.total_marks!;
    });

    return (
        <>
        <div className="min-h-screen bg-emerald-100 p-6">
            <div className="mx-auto max-w-6xl px-4 py-6">
                {/* Header card */}
                <div className={`${card} ${cardPad}`}>
                    <div className="flex items-center justify-between gap-3">
                        <div>
                            <div className="text-2xl font-extrabold tracking-tight text-slate-900">
                                Class Admin
                            </div>
                            <div className="text-sm text-slate-600">
                                Private teacher tools • First names only (GDPR friendly)
                            </div>
                        </div>

                        <button
                            type="button"
                            onClick={() => navigate(`/class/${classId}`)}
                            className="rounded-2xl border-2 border-slate-200 bg-white px-4 py-2 text-sm font-semibold hover:bg-slate-50"
                        >
                            Back to Class
                        </button>
                    </div>

                    {demoWorkspace && (
                        <section className="mt-5 overflow-hidden rounded-[30px] border border-white/80 bg-gradient-to-r from-cyan-50 via-white to-emerald-50 p-5 shadow-[0_16px_38px_rgba(15,23,42,0.08)]">
                            <div className="flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
                                <div className="flex items-center gap-4">
                                    <div className="flex h-16 w-16 shrink-0 items-center justify-center">
                                        <img src={ELogo2} alt="Elume" className="h-14 w-14 object-contain drop-shadow-[5px_7px_9px_rgba(15,23,42,0.14)]" />
                                    </div>
                                    <div className="max-w-2xl">
                                        <div className="text-xs font-black uppercase tracking-[0.16em] text-sky-700">{t("demo.cat4.label")}</div>
                                        <h2 className="mt-1 text-2xl font-extrabold tracking-tight text-slate-900">{t("demo.cat4.title")}</h2>
                                        <p className="mt-2 text-sm leading-6 text-slate-600">{t("demo.cat4.description")}</p>
                                    </div>
                                </div>
                                <button type="button" onClick={openDemoCat4} className="shrink-0 rounded-full border-2 border-sky-600 bg-sky-600 px-5 py-3 text-sm font-bold text-white shadow-[0_10px_22px_rgba(2,132,199,0.22)] transition hover:border-sky-700 hover:bg-sky-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-700">
                                    {t("demo.cat4.open")}
                                </button>
                            </div>
                        </section>
                    )}

                    {error && (
                        <InlineNotice
                            variant="error"
                            title="That didn’t quite work"
                            message={error}
                            className="mt-4"
                        />
                    )}

                    <div className="mt-4">
                        <div className="flex flex-wrap gap-2">
                            <button
                                type="button"
                                className={tab === "students" ? `${pill} border-emerald-400 bg-emerald-50` : pill}
                                onClick={() => setTab("students")}
                            >
                                Students
                            </button>

                            <button
                                type="button"
                                className={tab === "tests" ? `${pill} border-emerald-400 bg-emerald-50` : pill}
                                onClick={() => setTab("tests")}
                            >
                                Test Results
                            </button>

                            <button
                                type="button"
                                className={tab === "insights" ? `${pill} border-emerald-400 bg-emerald-50` : pill}
                                onClick={() => { setTab("insights"); navigate(demoClassInsightsPath(classId)); }}
                            >
                                Insights
                            </button>

                            {aacEnabled && (
                                <button
                                    type="button"
                                    className={tab === "aac" ? `${pill} border-teal-400 bg-teal-50` : pill}
                                    onClick={() => { setTab("aac"); navigate(`/class/${classId}/admin/aac`); }}
                                >
                                    AAC Tracker
                                </button>
                            )}

                            <button
                                type="button"
                                onClick={() => setTab("reports")}
                                className={`relative inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-semibold text-white shadow-md transition
    ${tab === "reports"
                                        ? "bg-gradient-to-r from-teal-500 via-blue-500 to-emerald-500"
                                        : "bg-gradient-to-r from-teal-400 via-blue-400 to-emerald-400 hover:from-teal-500 hover:via-blue-500 hover:to-emerald-500"
                                    }`}
                            >
                                <span className="relative flex items-center justify-center">

                                    {/* pulsing AI halo */}
                                    <span className="absolute inline-flex h-4 w-4 rounded-full bg-white opacity-40 animate-[ping_2s_ease-out_infinite]"></span>

                                    {/* brain icon */}
                                    <span className="relative text-xs">🧠</span>

                                </span>

                                Report Comments Generator
                            </button>
                        </div>

                        {tab === "tests" && (
                            <div className="mt-3 flex flex-wrap items-start justify-between gap-3">
                                <div>
                                    <div className="text-lg font-extrabold tracking-tight text-slate-900">
                                        Tests & Results
                                    </div>
                                    <div className="mt-1 text-sm text-slate-600">
                                        Create, edit and delete tests • Enter % per student • Mark absent • Clear saved results
                                    </div>
                                </div>

                                <button
                                    type="button"
                                    onClick={() => {
                                        if (demoReadOnly) {
                                            setError("This demo uses prepared assessments so you can explore results and progress. In your own Elume account, you can create assessments.");
                                            return;
                                        }
                                        setShowCreateTest(true);
                                    }}
                                    disabled={demoReadOnly}
                                    className="shrink-0 rounded-2xl border-2 border-slate-900 bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800"
                                >
                                    Input Results
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* Students tab */}
                {tab === "students" && (
                    <div className={`${card} ${cardPad} mt-6`}>
                        <div className="flex flex-wrap items-start justify-between gap-3">
                            <div>
                                <div className="text-lg font-extrabold tracking-tight text-slate-900">{t("classAdmin.studentRoster")}</div>
                                <div className="mt-1 text-sm text-slate-600">
                                    {t("classAdmin.rosterHelp")}
                                </div>
                            </div>

                            <div className="flex flex-wrap items-center gap-2">
                                <button
                                    type="button"
                                    onClick={generateStudentLink}
                                    className="rounded-2xl border-2 border-slate-200 bg-white px-4 py-2 text-sm font-semibold hover:bg-slate-50"
                                >
                                    {t("classAdmin.refreshStudentLink")}
                                </button>

                                {studentToken && (
                                    <button
                                        type="button"
                                        onClick={() =>
                                            navigator.clipboard.writeText(`${window.location.origin}/s/${studentToken}`).catch(() => { })
                                        }
                                        className="rounded-2xl border-2 border-slate-900 bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800"
                                    >
                                    {t("classAdmin.copyStudentLink")}
                                    </button>
                                )}
                            </div>
                        </div>

                        {studentToken && (
                            <div className="mt-4 rounded-2xl border-2 border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
                                {t("classAdmin.studentLinkReady")}
                            </div>
                        )}

                        {demoReadOnly ? (
                            <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm leading-6 text-amber-950" role="status">
                                {t("classAdmin.demoRosterLocked")}
                            </div>
                        ) : <>
                        <div className="mt-4 grid gap-3 md:grid-cols-3">
                            <input
                                value={firstName}
                                onChange={(e) => setFirstName(e.target.value)}
                                className="rounded-2xl border-2 border-slate-200 bg-white px-3 py-2"
                                placeholder={t("classAdmin.firstNamePlaceholder")}
                            />
                            <input
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                className="rounded-2xl border-2 border-slate-200 bg-white px-3 py-2"
                                placeholder={t("classAdmin.notesOptional")}
                            />
                            <button
                                type="button"
                                onClick={addStudent}
                                className="rounded-2xl border-2 border-slate-900 bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
                                disabled={!firstName.trim()}
                            >
                                {t("classAdmin.addStudent")}
                            </button>
                        </div>

                        <div className="mt-4 rounded-3xl border-2 border-slate-200 bg-slate-50 p-4">
                            <div className="text-sm font-extrabold text-slate-900">{t("classAdmin.addMultiple")}</div>
                            <div className="mt-1 text-sm text-slate-600">
                                {t("classAdmin.bulkHelp")}
                            </div>

                            <textarea
                                value={bulkNames}
                                onChange={(e) => setBulkNames(e.target.value)}
                                className="mt-3 h-32 w-full rounded-2xl border-2 border-slate-200 bg-white px-3 py-2"
                                placeholder={"Aoife\nLiam\nSarah\nJack"}
                            />

                            <div className="mt-3 flex justify-end">
                                <button
                                    type="button"
                                    onClick={addStudentsBulk}
                                    className="rounded-2xl border-2 border-slate-900 bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
                                    disabled={!bulkNames.trim()}
                                >
                                    {t("classAdmin.addStudents")}
                                </button>
                            </div>
                        </div>
                        </>}

                        <div className="mt-5">
                            {loading ? (
                                <div className="text-sm text-slate-600">{t("collab.loading")}</div>
                            ) : students.length === 0 ? (
                            <div className="text-sm text-slate-600">{t("classAdmin.noStudents")}</div>
                            ) : (
                                <div className="divide-y divide-slate-200 rounded-3xl border-2 border-slate-200 bg-white">
                                    {students.map((s) => (
                                        <div key={s.id} className="flex items-center justify-between gap-3 p-3">
                                            <div>
                                                <div className="font-semibold text-slate-900">
                                                    {s.first_name}
                                                    {!s.active && (
                                                        <span className="ml-2 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs text-slate-600">
                                                            inactive
                                                        </span>
                                                    )}
                                                </div>
                                                {!!s.notes && <div className="text-sm text-slate-600">{s.notes}</div>}
                                            </div>
                                            {!demoReadOnly && <div className="flex items-center gap-2">
                                                <button
                                                    type="button"
                                                    onClick={() => toggleActive(s.id, !s.active)}
                                                    className="rounded-2xl border-2 border-slate-200 bg-white px-4 py-2 text-sm font-semibold hover:bg-slate-50"
                                                >
                                                    {s.active ? "Deactivate" : "Activate"}
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setDeletingStudent(s)}
                                                    className="rounded-2xl border-2 border-rose-200 bg-rose-50 px-4 py-2 text-sm font-semibold text-rose-700 hover:bg-rose-100"
                                                >
                                                    Delete
                                                </button>
                                            </div>}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="mt-4 flex items-center justify-between text-sm text-slate-600">
                            <span>
                                Active students:{" "}
                                <span className="font-semibold text-slate-900">{activeCount}</span>
                            </span>

                            {inactiveCount > 0 && (
                                <span>
                                    Inactive: <span className="font-semibold text-slate-900">{inactiveCount}</span>
                                </span>
                            )}
                        </div>
                    </div>
                )}

                {/* Tests tab */}
                {tab === "tests" && (
                    <div className={`${card} ${cardPad} mt-6`}>
                        <div className="text-lg font-extrabold tracking-tight text-slate-900">{t("classAdmin.testsResults")}</div>
                        <div className="mt-1 text-sm text-slate-600">
                            Open a test to enter results, edit the test details, or delete it completely.
                        </div>

                        <div className="mt-4">
                            {loading ? (
                                <div className="text-sm text-slate-600">Loading…</div>
                            ) : tests.length === 0 ? (
                                <div className="text-sm text-slate-600">{t("classAdmin.noTests")}</div>
                            ) : (
                                <div className="divide-y divide-slate-200 rounded-3xl border-2 border-slate-200 bg-white">
                                    {tests.map((t) => (
                                        <div key={t.id} className="flex flex-wrap items-center justify-between gap-3 p-3">
                                            <div>
                                                <div className="font-semibold text-slate-900">{t.title}</div>
                                                <div className="text-sm text-slate-600">{t.assessment_date || "No date set"}</div>
                                            </div>

                                            <div className="flex flex-wrap items-center gap-2">
                                                <button
                                                    type="button"
                                                    className="rounded-2xl border-2 border-slate-200 bg-white px-4 py-2 text-sm font-semibold hover:bg-slate-50"
                                                    onClick={() => openResults(t.id)}
                                                >
                                                    Enter Results
                                                </button>

                                                <button
                                                    type="button"
                                                    className="rounded-2xl border-2 border-amber-200 bg-amber-50 px-4 py-2 text-sm font-semibold text-amber-900 hover:bg-amber-100"
                                                    onClick={() => openEditTestModal(t)}
                                                    disabled={demoReadOnly}
                                                >
                                                    Edit Test
                                                </button>

                                                <button
                                                    type="button"
                                                    className="rounded-2xl border-2 border-rose-200 bg-rose-50 px-4 py-2 text-sm font-semibold text-rose-800 hover:bg-rose-100"
                                                    onClick={() => deleteAssessment(t)}
                                                    disabled={demoReadOnly}
                                                >
                                                    Delete Test
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Insights tab */}
                {tab === "insights" && (
                    <div className={`${card} ${cardPad} mt-6`}>
                        <div className="flex flex-wrap items-start justify-between gap-3">
                            <div>
                                <div className="text-lg font-extrabold tracking-tight text-slate-900">{t("classAdmin.insights")}</div>
                                <div className="mt-1 text-sm text-slate-600">
                                    Class average • At-risk list • Rankings by average
                                </div>
                            </div>

                            <div className="flex flex-wrap items-center gap-2">
                                <button
                                    type="button"
                                    onClick={openClassReport}
                                    className="rounded-2xl border-2 border-sky-200 bg-sky-50 px-4 py-2 text-sm font-semibold text-sky-900 hover:bg-sky-100"
                                >
                                    Open Class Report
                                </button>

                                <button
                                    type="button"
                                    onClick={loadInsights}
                                    className="rounded-2xl border-2 border-slate-200 bg-white px-4 py-2 text-sm font-semibold hover:bg-slate-50"
                                >
                                    Refresh
                                </button>
                            </div>
                        </div>

                        {insightsError && (
                            <InlineNotice
                                variant="error"
                                title="That didn’t quite work"
                                message={insightsError}
                                actionLabel="Try again"
                                onAction={() => void loadInsights()}
                                className="mt-4"
                            />
                        )}

                        <section className="mt-5 border-t border-slate-200 pt-5" aria-label="AAC Tracker entry in Class Insights">
                            <AacPlannerPage
                                embedded
                                mode="entry"
                                onEnabledChange={setAacEnabled}
                                onOpen={() => { setTab("aac"); navigate(`/class/${classId}/admin/aac`); }}
                            />
                        </section>

                        <div className="mt-5 grid gap-4 md:grid-cols-3">
                            <div className="rounded-3xl border-2 border-slate-200 bg-white p-4">
                                <div className="text-sm font-semibold text-slate-600">{t("classAdmin.classAverage")}</div>
                                <div className="mt-2 flex items-end gap-3">
                                    <div className="text-4xl font-extrabold tracking-tight text-slate-900">
                                        {insightsLoading ? "…" : pct(insights?.class_average ?? null)}
                                    </div>

                                    <span
                                        className={`rounded-full border-2 px-3 py-1 text-xs font-semibold ${avgPillClass(
                                            insights?.class_average ?? null
                                        )}`}
                                        title="Colour bands: 85+ excellent, 70+ strong, 50+ watch, below 50 at-risk"
                                    >
                                        {insights?.assessment_count ?? 0} assessments
                                    </span>
                                </div>

                                <div className="mt-2 text-xs text-slate-500">
                                    Active students: {insights?.active_student_count ?? 0}
                                </div>
                            </div>

                            <div className="md:col-span-2 rounded-3xl border-2 border-slate-200 bg-white p-4">
                                <div className="flex items-center justify-between">
                                <div className="text-sm font-semibold text-slate-600">{t("classAdmin.atRisk")}</div>
                                    <div className="text-xs text-slate-500">{t("classAdmin.atRiskRule")}</div>
                                </div>

                                {insightsLoading ? (
                                    <div className="mt-3 text-sm text-slate-600">Loading…</div>
                                ) : !insights || sortedAtRiskStudents.length === 0 ? (
                                    <div className="mt-3 text-sm text-slate-600">{t("classAdmin.noAtRisk")}</div>
                                ) : (
                                    <div className="mt-3 divide-y divide-slate-200 rounded-2xl border border-slate-200">
                                        {sortedAtRiskStudents.slice(0, 6).map((r) => (
                                            <div key={r.student_id} className="flex items-start justify-between gap-3 p-3">
                                                <div>
                                                    <div className="font-semibold text-slate-900">{r.first_name}</div>
                                                    <div className="mt-1 text-xs text-slate-600">
                                                        {r.reasons.join(" • ")}
                                                    </div>
                                                </div>

                                                <div className="text-right text-xs text-slate-600">
                                                    <div>
                                                        Avg: <span className="font-semibold text-slate-900">{pct(r.average)}</span>
                                                    </div>
                                                    <div>
                                                        Missed: <span className="font-semibold text-slate-900">{r.missed}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="mt-5 rounded-3xl border-2 border-slate-200 bg-white p-4">
                            <div className="flex items-center justify-between">
                                <div className="text-sm font-semibold text-slate-600">{t("classAdmin.rankings")}</div>
                                <div className="text-xs text-slate-500">{t("classAdmin.rankingHelp")}</div>
                            </div>

                            {insightsLoading ? (
                                <div className="mt-3 text-sm text-slate-600">Loading…</div>
                            ) : !insights || insights.student_rankings.length === 0 ? (
                                <div className="mt-3 text-sm text-slate-600">{t("classAdmin.noResults")}</div>
                            ) : (
                                <div className="mt-3 overflow-hidden rounded-2xl border border-slate-200">
                                    <div className="grid grid-cols-12 items-center bg-slate-50 px-4 py-3 text-xs font-semibold text-slate-600">
                                        <div className="col-span-3">{t("classAdmin.name")}</div>
                                        <div className="col-span-2">{t("classAdmin.average")}</div>
                                        <div className="col-span-2">{t("classAdmin.latest")}</div>
                                        <div className="col-span-1">{t("classAdmin.taken")}</div>
                                        <div className="col-span-1">{t("classAdmin.missed")}</div>
                                        <div className="col-span-3 text-right">{t("classAdmin.report")}</div>
                                    </div>

                                    <div className="divide-y divide-slate-200">
                                        {insights.student_rankings.map((s) => {
                                            const t = trendFromLatest(s.average, s.latest);

                                            return (
                                                <div
                                                    key={s.student_id}
                                                    className="grid grid-cols-12 items-center px-4 py-3 text-sm"
                                                >
                                                    <div className="col-span-3 font-semibold text-slate-900">
                                                        <div className="col-span-3 font-semibold text-slate-900">
                                                            <button
                                                                type="button"
                                                                onClick={() => openStudentHistoryModal(s)}
                                                                className="underline decoration-dotted underline-offset-4 hover:text-emerald-700"
                                                            >
                                                                {s.first_name}
                                                            </button>
                                                        </div>
                                                    </div>

                                                    <div className="col-span-2">
                                                        <span
                                                            className={`inline-flex items-center gap-2 rounded-full border-2 px-3 py-1 text-xs font-semibold ${avgPillClass(
                                                                s.average
                                                            )}`}
                                                        >
                                                            {pct(s.average)}
                                                        </span>
                                                    </div>

                                                    <div className="col-span-2">
                                                        <span
                                                            className={`inline-flex items-center gap-1 text-xs font-semibold ${trendClass(t)}`}
                                                        >
                                                            <TrendIcon t={t} />
                                                            {s.latest == null ? "—" : `${s.latest.toFixed(0)}%`}
                                                            <span className="ml-1 font-normal">{trendText(t)}</span>
                                                        </span>
                                                    </div>

                                                    <div className="col-span-1 text-slate-700">{s.taken}</div>
                                                    <div className="col-span-1 text-slate-700">{s.missed}</div>

                                                    <div className="col-span-3 flex justify-end">
                                                        <button
                                                            type="button"
                                                            onClick={() => openStudentReport(s.student_id)}
                                                            className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-900 hover:bg-emerald-100"
                                                        >
                                                            {translate("classAdmin.openStudentReport")}
                                                        </button>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {tab === "aac" && (
                    <div className={`${card} ${cardPad} mt-6`}>
                        <AacPlannerPage embedded mode="workspace" onEnabledChange={setAacEnabled} />
                    </div>
                )}

                {tab === "reports" && (
                    <div className={`${card} ${cardPad} mt-6`}>
                        <div className="flex flex-wrap items-start justify-between gap-3">
                            <div>
                                <div className="text-lg font-extrabold tracking-tight text-slate-900">{t("classAdmin.aiComments")}</div>
                                <div className="mt-1 text-sm text-slate-600">
                                    {t("classAdmin.aiCommentsHelp")}
                                </div>
                            </div>

                            {demoReadOnly ? (
                                <div className="rounded-full border border-violet-100 bg-violet-50 px-3 py-1.5 text-xs font-bold text-violet-900">
                                    {t("demo.reportExamples")}
                                </div>
                            ) : null}
                        </div>

                        {demoReadOnly ? (
                            <div className="mt-4 rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm leading-6 text-emerald-950">
                                {t("demo.reportExamplesHelp")}
                            </div>
                        ) : <AiAssistanceNotice variant="input" className="mt-4" />}

                        {reportError && (
                            <InlineNotice
                                variant="error"
                                title="That didn’t quite work"
                                message={reportError}
                                className="mt-4"
                            />
                        )}

                        {reportQuotaNotice && (
                            <div className="mt-3 text-sm font-semibold text-slate-500">{reportQuotaNotice}</div>
                        )}

                        <div className="mt-5 space-y-4">
                            {students.length === 0 ? (
                                <div className="text-sm text-slate-600">No students yet.</div>
                            ) : (
                                students
                                    .filter((s) => s.active)
                                    .map((s) => {
                                        const draft = reportDrafts[s.id] || {
                                            length: "Medium" as ReportLength,
                                            indicators: [],
                                            signOff: "",
                                            comment: "",
                                        };

                                        const insight = insightByStudent.get(s.id);

                                        return (
                                            <div key={s.id} className="rounded-3xl border-2 border-slate-200 bg-white p-4">
                                                <div className="flex flex-wrap items-start justify-between gap-3">
                                                    <div>
                                                        <div className="text-base font-extrabold tracking-tight text-slate-900">
                                                            {s.first_name}
                                                        </div>
                                                        <div className="mt-1 text-xs text-slate-600">
                                                            Avg: {insight?.average == null ? "—" : `${insight.average}%`}
                                                            <span className="mx-2 text-slate-300">•</span>
                                                            Latest: {insight?.latest == null ? "—" : `${insight.latest}%`}
                                                            <span className="mx-2 text-slate-300">•</span>
                                                            Taken: {insight?.taken ?? 0}
                                                            <span className="mx-2 text-slate-300">•</span>
                                                            Missed: {insight?.missed ?? 0}
                                                        </div>
                                                    </div>

                                                    <div className="flex items-center gap-2">
                                                        <select
                                                            value={draft.length}
                                                            onChange={(e) =>
                                                                updateReportDraft(s.id, {
                                                                    length: e.target.value as ReportLength,
                                                                })
                                                            }
                                                            className="rounded-2xl border-2 border-slate-200 bg-white px-3 py-2 text-sm"
                                                            disabled={demoReadOnly}
                                                        >
                                                            <option value="Short">{t("classAdmin.short")}</option>
                                                            <option value="Medium">{t("classAdmin.medium")}</option>
                                                            <option value="Long">{t("classAdmin.long")}</option>
                                                        </select>

                                                        <button
                                                            type="button"
                                                            onClick={() => generateReportComment(s.id)}
                                                            disabled={generatingReportFor === s.id}
                                                            className="rounded-2xl border-2 border-slate-900 bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
                                                        >
                                    {generatingReportFor === s.id ? t("classAdmin.generating") : t("classAdmin.generateComment")}
                                                        </button>
                                                    </div>
                                                </div>

                                                <div className="mt-4">
                                                    <div className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">
                                                        {demoReadOnly ? t("demo.reportIndicators") : t("classAdmin.indicators")}
                                                    </div>

                                                    <div className="flex flex-wrap gap-2">
                                                        {REPORT_INDICATORS.map((label) => {
                                                            const active = draft.indicators.includes(label);

                                                            return (
                                                                <button
                                                                    key={label}
                                                                    type="button"
                                                                    onClick={() => toggleReportIndicator(s.id, label)}
                                                                    disabled={demoReadOnly}
                                                                    className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition ${active
                                                                        ? "border-emerald-300 bg-emerald-50 text-emerald-900"
                                                                        : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                                                                        }`}
                                                                >
                                                                    {label}
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                </div>

                                                <div className="mt-4 grid gap-3 md:grid-cols-3">
                                                    <div className="md:col-span-3">
                                                        <label className="text-xs font-bold uppercase tracking-wide text-slate-500">
                                                            {t("classAdmin.finalSignOff")}
                                                        </label>
                                                        {demoReadOnly ? (
                                                            <div className="mt-1 rounded-2xl border-2 border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-500">
                                                                {t("demo.reportExamples")}
                                                            </div>
                                                        ) : (
                                                            <input
                                                                value={draft.signOff}
                                                                onChange={(e) => updateReportDraft(s.id, { signOff: e.target.value })}
                                                                placeholder={t("classAdmin.signOffPlaceholder")}
                                                                className="mt-1 w-full rounded-2xl border-2 border-slate-200 bg-white px-3 py-2 text-sm"
                                                            />
                                                        )}
                                                    </div>

                                                    <div className="md:col-span-3">
                                                        <div className="flex items-center justify-between">
                                                            <label className="text-xs font-bold uppercase tracking-wide text-slate-500">
                                                                {t("classAdmin.comment")}
                                                            </label>

                                                            <button
                                                                type="button"
                                                                onClick={() => navigator.clipboard.writeText(draft.comment || "").catch(() => { })}
                                                                disabled={!draft.comment.trim()}
                                                                className="flex h-7 w-7 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-600 shadow-sm transition hover:bg-slate-50 hover:text-slate-900 disabled:opacity-40"
                                                                title={t("classAdmin.copyComment")}
                                                            >
                                                                ⧉
                                                            </button>
                                                        </div>

                                                        {draft.aiGenerated && <AiAssistanceNotice variant="output" className="mb-2" />}

                                                        {demoReadOnly ? (
                                                            <div className="mt-1 select-text whitespace-pre-wrap rounded-2xl border-2 border-slate-200 bg-slate-50 px-4 py-3 text-sm leading-6 text-slate-800">
                                                                {draft.comment}
                                                            </div>
                                                        ) : (
                                                            <textarea
                                                                value={draft.comment}
                                                                onChange={(e) => updateReportDraft(s.id, { comment: e.target.value })}
                                                                rows={4}
                                                                className="mt-1 w-full rounded-2xl border-2 border-slate-200 bg-white px-3 py-2 text-sm"
                                                                placeholder={t("classAdmin.commentPlaceholder")}
                                                            />
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })
                            )}
                        </div>
                    </div>
                )}

                {selectedHistoryStudent && (
                    <div
                        className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4"
                        onClick={() => setSelectedHistoryStudent(null)}
                    >
                        <div
                            className="w-full max-w-2xl rounded-3xl border-2 border-slate-200 bg-white p-5 shadow-xl"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="flex items-start justify-between gap-4">
                                <div>
                                    <div className="text-xl font-extrabold tracking-tight text-slate-900">
                                        {selectedHistoryStudent.first_name}
                                    </div>
                                    <div className="mt-1 text-sm text-slate-600">
                                        Latest: <b>{selectedHistoryStudent.latest ?? "—"}</b>% · Avg:{" "}
                                        <b>{selectedHistoryStudent.average ?? "—"}</b>% · Taken:{" "}
                                        <b>{selectedHistoryStudent.taken}</b> · Missed:{" "}
                                        <b>{selectedHistoryStudent.missed}</b>
                                    </div>
                                </div>

                                <div className="flex shrink-0 items-center gap-2">
                                    {!demoReadOnly && <button
                                        type="button"
                                        onClick={() => openStudentNameEditor(selectedHistoryStudent)}
                                        className="min-h-9 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50"
                                    >
                                        {t("classAdmin.editStudentName")}
                                    </button>}
                                    <button
                                        type="button"
                                        onClick={() => setSelectedHistoryStudent(null)}
                                        className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-lg text-slate-600 shadow-sm hover:bg-slate-50 hover:text-slate-900"
                                        title={t("common.close")}
                                    >
                                        ×
                                    </button>
                                </div>
                            </div>

                            <div className="mt-4 rounded-2xl border border-slate-200 p-3">
                                {historyLoading[selectedHistoryStudent.student_id] && (
                                    <div className="text-sm text-slate-500">{t("collab.loading")}</div>
                                )}

                                {!historyLoading[selectedHistoryStudent.student_id] &&
                                    historyCache[selectedHistoryStudent.student_id]?.points?.length ? (
                                    <>
                                        <PerformanceChart
                                            points={historyCache[selectedHistoryStudent.student_id]!.points}
                                        />

                                        <div className="mt-3 max-h-72 overflow-auto rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">
                                            {historyCache[selectedHistoryStudent.student_id]!.points
                                                .slice(-10)
                                                .slice()
                                                .reverse()
                                                .map((p) => {
                                                    const delta =
                                                        typeof p.student === "number" &&
                                                            typeof p.class_avg === "number"
                                                            ? Math.round((p.student - p.class_avg) * 10) / 10
                                                            : null;

                                                    return (
                                                        <div
                                                            key={p.assessment_id}
                                                            className="flex items-center justify-between gap-3 border-b border-slate-200/70 py-2 last:border-b-0"
                                                        >
                                                            <div className="min-w-0">
                                                                <div className="truncate text-sm font-semibold text-slate-800">
                                                                    {p.title}
                                                                </div>
                                                                <div className="text-xs text-slate-500">
                                                                    {p.date ?? ""}
                                                                </div>
                                                            </div>

                                                            <div className="shrink-0 text-right">
                                                                <div className="text-sm font-extrabold text-slate-900">
                                                                    {p.absent
                                                                        ? "Absent"
                                                                        : p.student == null
                                                                            ? "—"
                                                                            : `${p.student}%`}
                                                                </div>

                                                                <div className="text-xs text-slate-500">
                                                                    {typeof p.class_avg === "number"
                                                                        ? `avg ${p.class_avg}%`
                                                                        : "avg —"}
                                                                    {delta != null && (
                                                                        <span
                                                                            className={
                                                                                delta >= 0
                                                                                    ? "ml-1 text-emerald-700"
                                                                                    : "ml-1 text-rose-700"
                                                                            }
                                                                        >
                                                                            ({delta >= 0 ? "+" : ""}
                                                                            {delta})
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                        </div>
                                    </>
                                ) : null}

                                {!historyLoading[selectedHistoryStudent.student_id] &&
                                    !historyCache[selectedHistoryStudent.student_id]?.points?.length && (
                                        <div className="text-sm text-slate-500">{t("classAdmin.noHistory")}</div>
                                    )}
                            </div>
                        </div>
                    </div>
                )}

                {editingStudentName && (
                    <div
                        className="fixed inset-0 z-[60] grid place-items-center bg-black/40 p-4"
                        onClick={closeStudentNameEditor}
                    >
                        <form
                            className="w-full max-w-md rounded-3xl border-2 border-slate-200 bg-white p-5 shadow-xl"
                            onClick={(event) => event.stopPropagation()}
                            onSubmit={(event) => {
                                event.preventDefault();
                                void saveStudentName();
                            }}
                        >
                            <h2 className="text-lg font-extrabold text-slate-900">{t("classAdmin.editStudentName")}</h2>
                            <p className="mt-1 text-sm text-slate-600">{t("classAdmin.editStudentNameHelp")}</p>

                            <label className="mt-4 block text-sm font-semibold text-slate-800" htmlFor="class-admin-student-name">
                                {t("classAdmin.studentName")}
                            </label>
                            <input
                                id="class-admin-student-name"
                                autoFocus
                                value={editedStudentName}
                                onChange={(event) => setEditedStudentName(event.target.value)}
                                disabled={savingStudentName}
                                className="mt-1 min-h-11 w-full rounded-xl border border-slate-300 px-3 py-2 text-base text-slate-900 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 disabled:bg-slate-100"
                            />

                            {studentNameError && (
                                <p role="alert" className="mt-3 text-sm font-medium text-rose-700">
                                    {studentNameError}
                                </p>
                            )}

                            <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                                <button
                                    type="button"
                                    onClick={closeStudentNameEditor}
                                    disabled={savingStudentName}
                                    className="min-h-11 rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                                >
                                    {t("common.cancel")}
                                </button>
                                <button
                                    type="submit"
                                    disabled={savingStudentName}
                                    className="min-h-11 rounded-xl bg-emerald-700 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-800 disabled:cursor-not-allowed disabled:opacity-60"
                                >
                                    {savingStudentName ? t("common.saving") : t("common.save")}
                                </button>
                            </div>
                        </form>
                    </div>
                )}

                {/* Create Test Modal */}
                {showCreateTest && (
                    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4">
                        <div className="w-full max-w-lg rounded-3xl border-2 border-slate-200 bg-white p-5 shadow-xl">
                            <div className="flex items-center justify-between">
                                <div className="text-lg font-extrabold text-slate-900">{t("classAdmin.createTest")}</div>
                                <button
                                    type="button"
                                    onClick={() => setShowCreateTest(false)}
                                    className="rounded-2xl border-2 border-slate-200 bg-white px-3 py-2 text-sm font-semibold hover:bg-slate-50"
                                >
                                    {t("common.close")}
                                </button>
                            </div>

                            <div className="mt-4 grid gap-3">
                                <label className="text-sm font-semibold text-slate-700">
                                    {t("classAdmin.testTitle")}
                                    <input
                                        value={newTestTitle}
                                        onChange={(e) => setNewTestTitle(e.target.value)}
                                        className="mt-1 w-full rounded-2xl border-2 border-slate-200 px-3 py-2"
                                        placeholder="e.g. Waves Test"
                                    />
                                </label>

                                <label className="text-sm font-semibold text-slate-700">
                                    {t("classAdmin.dateOptional")}
                                    <input
                                        type="date"
                                        value={newTestDate}
                                        onChange={(e) => setNewTestDate(e.target.value)}
                                        className="mt-1 w-full rounded-2xl border-2 border-slate-200 px-3 py-2"
                                    />
                                </label>

                                <label className="text-sm font-semibold text-slate-700">
                                    {t("classAdmin.totalMarks")}
                                    <input type="number" inputMode="decimal" min="0.01" step="any" value={newTestTotalMarks} onWheel={(event) => event.currentTarget.blur()} onChange={(e) => setNewTestTotalMarks(e.target.value)} className="mt-1 min-h-11 w-full rounded-2xl border-2 border-slate-200 px-3 py-2" />
                                </label>

                                <div className="mt-2 flex justify-end">
                                    <button
                                        type="button"
                                        onClick={createAssessment}
                                        disabled={!newTestTitle.trim()}
                                        className="rounded-2xl border-2 border-slate-900 bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
                                    >
                                        {t("tests.create")}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Edit Test Modal */}
                {showEditTest && editingTest && (
                    <div
                        className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4"
                        onKeyDown={(event) => {
                            if (event.key === "Escape" && !savingEditTest) {
                                event.preventDefault();
                                closeEditTestModal();
                            }
                        }}
                    >
                        <div className="w-full max-w-lg rounded-3xl border-2 border-slate-200 bg-white p-5 shadow-xl" role="dialog" aria-modal="true" aria-labelledby="edit-assessment-heading">
                            <div className="flex items-center justify-between">
                                <h2 id="edit-assessment-heading" className="text-lg font-extrabold text-slate-900">{confirmingTotalMarksChange ? t("classAdmin.updateTotalMarksTitle") : t("classAdmin.editTest")}</h2>
                                <button
                                    type="button"
                                    onClick={closeEditTestModal}
                                    disabled={savingEditTest}
                                    className="rounded-2xl border-2 border-slate-200 bg-white px-3 py-2 text-sm font-semibold hover:bg-slate-50"
                                >
                                    {t("common.close")}
                                </button>
                            </div>

                            {confirmingTotalMarksChange ? (
                                <div className="mt-4 grid gap-4">
                                    <p className="text-sm leading-6 text-slate-600">
                                        {t("classAdmin.updateTotalMarksBody")
                                            .replace("{{oldTotal}}", String(editingTest.total_marks))
                                            .replace("{{newTotal}}", editTestTotalMarks)}
                                    </p>
                                    {editTestError && <div role="alert" className="rounded-2xl border-2 border-red-200 bg-red-50 px-3 py-2 text-sm font-semibold text-red-700">{editTestError}</div>}
                                    <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                                        <button
                                            type="button"
                                            autoFocus
                                            onClick={() => {
                                                setConfirmingTotalMarksChange(false);
                                                setEditTestError(null);
                                            }}
                                            disabled={savingEditTest}
                                            className="rounded-2xl border-2 border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                                        >
                                            {t("classAdmin.goBack")}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => saveEditedTest(true)}
                                            disabled={savingEditTest}
                                            className="rounded-2xl border-2 border-slate-900 bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
                                        >
                                            {savingEditTest ? t("common.saving") : t("classAdmin.updateTotal")}
                                        </button>
                                    </div>
                                </div>
                            ) : (
                            <div className="mt-4 grid gap-3">
                                <label className="text-sm font-semibold text-slate-700">
                                    {t("classAdmin.testTitle")}
                                    <input
                                        autoFocus
                                        value={editTestTitle}
                                        onChange={(e) => setEditTestTitle(e.target.value)}
                                        className="mt-1 w-full rounded-2xl border-2 border-slate-200 px-3 py-2"
                                        placeholder="e.g. Waves Test"
                                    />
                                </label>

                                <label className="text-sm font-semibold text-slate-700">
                                    {t("classAdmin.dateOptional")}
                                    <input
                                        type="date"
                                        value={editTestDate}
                                        onChange={(e) => setEditTestDate(e.target.value)}
                                        className="mt-1 w-full rounded-2xl border-2 border-slate-200 px-3 py-2"
                                    />
                                </label>

                                {editingTest.total_marks == null && (editingTest.result_count || 0) > 0 ? (
                                    <p className="rounded-xl bg-slate-50 p-3 text-sm text-slate-600">{t("classAdmin.legacyPercentageOnlyHelp")}</p>
                                ) : (
                                    <label className="text-sm font-semibold text-slate-700">
                                        {t("classAdmin.totalMarks")}
                                        <input type="number" inputMode="decimal" min="0.01" step="any" value={editTestTotalMarks} onWheel={(event) => event.currentTarget.blur()} onChange={(e) => setEditTestTotalMarks(e.target.value)} className="mt-1 min-h-11 w-full rounded-2xl border-2 border-slate-200 px-3 py-2" />
                                    </label>
                                )}

                                {editTestError && <div role="alert" className="rounded-2xl border-2 border-red-200 bg-red-50 px-3 py-2 text-sm font-semibold text-red-700">{editTestError}</div>}
                                <div className="mt-2 flex justify-end">
                                    <button
                                        type="button"
                                        onClick={() => saveEditedTest()}
                                        disabled={!editTestTitle.trim() || savingEditTest}
                                        className="rounded-2xl border-2 border-slate-900 bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
                                    >
                                        {savingEditTest ? t("common.saving") : t("common.saveChanges")}
                                    </button>
                                </div>
                            </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Enter Results Modal */}
                {showResults && selectedAssessment && (
                    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4">
                        <div className="w-full max-w-4xl rounded-3xl border-2 border-slate-200 bg-white p-5 shadow-xl" role="dialog" aria-modal="true" aria-labelledby="assessment-results-heading">
                            <div className="flex items-start justify-between gap-3">
                                <div>
                                    <h2 id="assessment-results-heading" className="text-lg font-extrabold text-slate-900">
                                        {selectedAssessment.title}
                                    </h2>
                                    <div className="text-sm text-slate-600">
                                        {selectedAssessment.assessment_date || ""}
                                    </div>
                                    <div className="mt-1 text-sm font-semibold text-slate-700">
                                        {selectedAssessment.total_marks == null ? t("classAdmin.legacyPercentageOnly") : `${t("classAdmin.totalMarks")}: ${selectedAssessment.total_marks}`}
                                    </div>
                                </div>

                                <div className="flex items-center gap-2">
                                    <button
                                        type="button"
                                        onClick={() => openEditTestModal(selectedAssessment, true)}
                                        disabled={demoReadOnly}
                                        className="rounded-2xl border-2 border-amber-200 bg-amber-50 px-3 py-2 text-sm font-semibold text-amber-900 hover:bg-amber-100"
                                    >
                                        {t("classAdmin.editTest")}
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() => {
                                            setShowResults(false);
                                            setSelectedAssessment(null);
                                            setResultRows([]);
                                        }}
                                        className="rounded-2xl border-2 border-slate-200 bg-white px-3 py-2 text-sm font-semibold hover:bg-slate-50"
                                    >
                                        {t("common.close")}
                                    </button>

                                    <button
                                        type="button"
                                        onClick={saveResults}
                                        disabled={savingResults || demoReadOnly}
                                        className="rounded-2xl border-2 border-slate-900 bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
                                    >
                                        {savingResults ? t("common.saving") : t("common.save")}
                                    </button>
                                </div>
                            </div>

                            {resultDraftNotice && <div role="status" className="mt-4 rounded-2xl border-2 border-sky-200 bg-sky-50 px-3 py-2 text-sm font-semibold text-sky-900">{resultDraftNotice}</div>}
                            {hasRawDraftAboveTotal && <div role="alert" className="mt-4 rounded-2xl border-2 border-amber-200 bg-amber-50 px-3 py-2 text-sm font-semibold text-amber-900">{t("classAdmin.rawMarksExceedTotal")}</div>}

                            <div className="mt-4 grid gap-3 md:grid-cols-3">
                                <div className="rounded-2xl border-2 border-slate-200 bg-slate-50 p-3">
                                    <div className="text-xs font-semibold text-slate-500">{t("classAdmin.students")}</div>
                                    <div className="mt-1 text-2xl font-extrabold text-slate-900">{resultRows.length}</div>
                                </div>

                                <div className="rounded-2xl border-2 border-slate-200 bg-slate-50 p-3">
                                    <div className="text-xs font-semibold text-slate-500">{t("classAdmin.enteredAbsent")}</div>
                                    <div className="mt-1 text-2xl font-extrabold text-slate-900">{resultsEnteredCount}</div>
                                </div>

                                <div className="rounded-2xl border-2 border-slate-200 bg-slate-50 p-3">
                                    <div className="text-xs font-semibold text-slate-500">{t("classAdmin.currentAverage")}</div>
                                    <div className={`mt-1 text-2xl font-extrabold ${avgClass(modalAverage)}`}>
                                        {modalAverage == null ? "—" : `${modalAverage.toFixed(1)}%`}
                                    </div>
                                </div>
                            </div>

                            <div className="mt-4 overflow-hidden rounded-3xl border-2 border-slate-200">
                                <div className="grid grid-cols-12 gap-2 border-b-2 border-slate-200 bg-slate-50 px-3 py-2 text-xs font-bold text-slate-600">
                                    <div className="col-span-4">{t("classAdmin.students")}</div>
                                    <div className="col-span-3">{selectedAssessment.total_marks == null ? t("classAdmin.result") : t("classAdmin.markOutOf").replace("{{total}}", String(selectedAssessment.total_marks))}</div>
                                    <div className="col-span-2">{t("classAdmin.percentage")}</div>
                                    <div className="col-span-1 text-center">{t("classAdmin.absent")}</div>
                                    <div className="col-span-2 text-right">{t("classAdmin.actions")}</div>
                                </div>

                                <div className="max-h-[60vh] overflow-auto">
                                    {resultRows.map((r) => (
                                        <div
                                            key={r.student_id}
                                            className="grid grid-cols-12 items-center gap-2 border-b border-slate-100 px-3 py-2"
                                        >
                                            <div className="col-span-4 font-semibold text-slate-900">{r.first_name}</div>

                                            <div className="col-span-3">
                                                <input
                                                    type="number"
                                                    inputMode="decimal"
                                                    min={0}
                                                    max={selectedAssessment.total_marks ?? 100}
                                                    step="any"
                                                    disabled={r.absent || demoReadOnly}
                                                    value={markDrafts[r.student_id] ?? ""}
                                                    onChange={(e) => setRowScore(r.student_id, e.target.value)}
                                                    onWheel={(event) => event.currentTarget.blur()}
                                                    aria-invalid={selectedAssessment.total_marks != null && !r.absent && (() => {
                                                        const draft = markDrafts[r.student_id];
                                                        return draft !== undefined && Number.isFinite(Number(draft)) && Number(draft) > selectedAssessment.total_marks;
                                                    })()}
                                                    className={`hide-number-spin w-full rounded-2xl border-2 px-3 py-2 text-sm disabled:bg-slate-100 ${selectedAssessment.total_marks != null && !r.absent && (() => {
                                                        const draft = markDrafts[r.student_id];
                                                        return draft !== undefined && Number.isFinite(Number(draft)) && Number(draft) > selectedAssessment.total_marks;
                                                    })() ? "border-amber-400 bg-amber-50" : "border-slate-200"}`}
                                                    placeholder={t("classAdmin.scorePlaceholder")}
                                                />
                                            </div>

                                            <div className="col-span-2 text-sm font-semibold text-slate-700">{r.score_percent == null ? "—" : `${r.score_percent.toFixed(2).replace(/\.00$/, "")}%`}</div>

                                            <div className="col-span-1 flex justify-center">
                                                <button
                                                    type="button"
                                                    onClick={() => toggleAbsent(r.student_id)}
                                                    disabled={demoReadOnly}
                                                    className={
                                                        r.absent
                                                            ? "rounded-2xl border-2 border-red-300 bg-red-50 px-4 py-2 text-sm font-semibold text-red-700"
                                                            : "rounded-2xl border-2 border-slate-200 bg-white px-4 py-2 text-sm font-semibold hover:bg-slate-50"
                                                    }
                                                >
                                                    {r.absent ? t("classAdmin.yes") : t("classAdmin.no")}
                                                </button>
                                            </div>

                                            <div className="col-span-2 flex justify-end">
                                                <button
                                                    type="button"
                                                    onClick={() => clearStudentResult(r.student_id)}
                                                    disabled={demoReadOnly}
                                                    className="rounded-2xl border-2 border-rose-200 bg-rose-50 px-4 py-2 text-sm font-semibold text-rose-800 hover:bg-rose-100"
                                                >
                                                    {t("classAdmin.clearResult")}
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="mt-3 text-xs text-slate-500">
                                Tip: leaving a score blank keeps it empty; “Absent” excludes them from averages; “Clear Result” removes a saved result for that student when you click Save.
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>

        {deletingStudent && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4">
                <div className="w-full max-w-lg rounded-2xl border-2 border-slate-200 bg-white p-5">
                    <div className="text-xl font-semibold">{t("classAdmin.deleteStudent")}</div>
                    <div className="mt-2 text-sm text-slate-600">
                        Deleting {deletingStudent.first_name} will also permanently remove their saved results and result history for this class.
                        Please print or save any report you may need before continuing.
                    </div>

                    <div className="mt-5 flex justify-end gap-2">
                        <button
                            type="button"
                            className="rounded-2xl border-2 border-slate-200 bg-white px-4 py-2 text-sm font-semibold hover:bg-slate-50"
                            onClick={() => setDeletingStudent(null)}
                            disabled={deleteBusy}
                        >
                            {t("common.cancel")}
                        </button>
                        <button
                            type="button"
                            className="rounded-2xl border-2 border-rose-700 bg-rose-600 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-700 disabled:opacity-60"
                            onClick={() => void confirmDeleteStudent()}
                            disabled={deleteBusy}
                        >
                            {deleteBusy ? t("classAdmin.deleting") : t("classAdmin.deleteStudentAction")}
                        </button>
                    </div>
                </div>
            </div>
        )}
        </>
    );
}
